import * as React from "react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"

interface Option {
  key: string
  text: string
}

interface AppDropdownProps {
  label?: string
  options: Option[]
  placeholder?: string
  defaultValue?: string
  disabled?: boolean
  onChange?: (event: React.MouseEvent<HTMLDivElement>, option: Option) => void
  buttonClassName?: string
  contentClassName?: string
  maxHeight?: string 
}

export const AppDropdown: React.FC<AppDropdownProps> = ({
  label,
  options,
  placeholder = "Select an option",
  defaultValue,
  disabled = false,
  onChange,
  buttonClassName,
  contentClassName,
  maxHeight = "10rem",
}) => {
  const [selected, setSelected] = React.useState<Option | null>(
    options.find((o) => o.key === defaultValue) || null
  )
  const [open, setOpen] = React.useState(false)

  const handleSelect = (
    e: React.MouseEvent<HTMLDivElement>,
    option: Option
  ) => {
    setSelected(option)
    onChange?.(e, option)
    setOpen(false)
  }

  return (
    <div className="flex flex-col gap-1 w-fit">
      {label && (
        <label className="text-sm font-medium text-gray-700">{label}</label>
      )}

      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button
            variant="outline"
            className={cn(
              "flex items-center justify-between gap-2 w-48 px-3 py-2 rounded-2xl border border-gray-300 bg-white text-sm text-gray-500 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-blue-200 transition-all duration-150",
              disabled && "opacity-50 cursor-not-allowed",
              buttonClassName
            )}
          >
            <span className="flex-1 truncate text-left" title={selected?.text}>
              {selected?.text || placeholder}
            </span>
            <ChevronDown
              size={16}
              className={cn(
                "flex-shrink-0 transition-transform text-gray-500",
                open && "rotate-180 text-gray-700"
              )}
            />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          className={cn(
            "rounded-2xl border border-gray-200 bg-white shadow-xl p-1 w-48 overflow-hidden",
            contentClassName
          )}
        >
          <DropdownMenuLabel className="text-xs text-gray-500 px-2 py-1">
            {label || "Options"}
          </DropdownMenuLabel>

          <div
            className="overflow-y-auto scrollbar-hide"
            style={{ maxHeight }}
          >
            {options.map((option) => (
              <DropdownMenuItem
                key={option.key}
                onClick={(e) => handleSelect(e, option)}
                className={cn(
                  "cursor-pointer px-2 py-1.5 text-sm rounded-xl text-gray-600 hover:bg-blue-50 hover:text-blue-500 focus:bg-blue-50",
                  selected?.key === option.key && "bg-blue-50 text-blue-500"
                )}
                title={option.text}
              >
                <span className="block truncate">{option.text}</span>
              </DropdownMenuItem>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
